<script lang="ts">
    import {onMount, setContext} from 'svelte';
    import axios, {type AxiosInstance, type AxiosResponse} from 'axios';

    import {
        dyData as dyDataStore,
        customer as customerStore,
        modal,
        analytics,
        popupData,
        priceData,
    } from 'general/store';
    import {ANALYTICS, COOKIE_VALUE, PLATFORM, VIEW} from 'general/constants';
    import {getCookie} from 'general/libs/getCookie';
    import {
        getSortedDyData,
        setDySelectors,
        getDyDataBySelector,
    } from 'general/libs/dy/normalizeResponse';
    import {getData} from 'general/libs/dy/getData';
    import {isCrawler} from 'general/libs/dy/helpers';
    import {key} from 'general/context';
    import type {
        ExtraPropsType,
        CustomerDataType,
        ContextType,
        TrustpilotType,
    } from 'types/general';
    import type {DyCustomJsonResponseType, DyRequestDataType} from 'types/dy';
    import {
        exitPopupTimer,
        openExitPopup,
        expSetRightSide,
        initializeTriggerPopup,
    } from 'general/libs/popup';
    import BodyDesktop from '../body/BodyDesktop.svelte';
    import MiniReminder from '../reminders/miniReminder/MiniReminder.svelte';
    import {sendAnalyticsPerDay} from 'general/libs/pushDataLayer';
    import {trustpilotStore} from 'general/store/trustpilot';
    import {logWithSentry} from 'sentry/sentry';
    import {getPrice} from 'general/libs/getPrice';

    export let dyRequestData: DyRequestDataType = {
        selectors: ['D_Popup', 'D_E_Popup', 'D_T_Popup'],
        context: {page: {type: 'OTHER'}},
    };
    export let instance: AxiosInstance = axios;

    instance.defaults.timeout = 0;
    export let instanceDY: AxiosInstance = axios;
    export let extraProps: ExtraPropsType = {};
    export let onEmailSubscribed = () => {};
    export let onEmailRejected = () => {};
    export let customer: CustomerDataType = {
        firstname: '',
        lastname: '',
        email: '',
        isSubscribed: false,
        isLoggedIn: false,
    };
    export let trustpilot: TrustpilotType;
    let timer: ReturnType<typeof setTimeout>;

    modal.changePopupSelectors(setDySelectors(dyRequestData.selectors));
    modal.changePlatform(PLATFORM.DESKTOP);

    $: if (trustpilot) {
        trustpilotStore.set(trustpilot);
    }

    // TODO: Requires an extension with the ability to re-request data
    const handlePopupOpen = () => {};

    $: isTryonOpened = false;
    const listener = (e: CustomEvent<boolean>) => {
        isTryonOpened = e.detail;
    };

    const handleExitPopup = (event: MouseEvent) => {
        if (event.clientY <= 0 && !isTryonOpened) {
            clearTimeout(exitPopupTimer);
            openExitPopup(true);
        }
    };

    const clearSubscriptions = () => {
        clearTimeout(timer);
        clearTimeout(exitPopupTimer);
        window.removeEventListener('mouseout', handleExitPopup);
        // eslint-disable-next-line no-undef
        window.removeEventListener('dynamicYield:EPopupOpen', listener as EventListener);
        window.removeEventListener('mfPopup:unMount', clearSubscriptions);
    };

    onMount(() => {
        // eslint-disable-next-line no-undef
        window.addEventListener('dynamicYield:EPopupOpen', listener as EventListener);
        window.addEventListener('mfPopup:unMount', clearSubscriptions);

        return () => {
            clearSubscriptions();
        };
    });

    onMount(() => {
        try {
            const isPreviouslySubscribed = getCookie(COOKIE_VALUE.SUBSCRIBER);
            const isPreviouslySubscribedPhone = getCookie(COOKIE_VALUE.SUBSCRIBER_PHONE);

            if (isCrawler() || (isPreviouslySubscribedPhone && isPreviouslySubscribed)) {
                return;
            }

            customerStore.setData(customer);

            if ($modal.isInitialized) modal.hideCurrentPopup();

            getData(instanceDY, dyRequestData, extraProps).then(
                (response: AxiosResponse<DyCustomJsonResponseType>) => {
                    const data = getSortedDyData(getDyDataBySelector(response.data));

                    popupData.set(data);

                    const welcomeSelector = $modal.popupSelectors.welcomePopup;
                    const triggerSelector = $modal.popupSelectors.triggerPopup;
                    const exitSelector = $modal.popupSelectors.exitPopup;

                    const isWelcomeTemplate = $popupData.value?.[welcomeSelector]?.template;
                    const isTriggerTemplate = $popupData.value?.[triggerSelector]?.template;

                    if (
                        $popupData.value?.[welcomeSelector]?.hasPrice ||
                        $popupData.value?.[exitSelector]?.hasPrice
                    ) {
                        getPrice(instance).then(price => {
                            priceData.set(`$${2 * Number(price)}`);
                        });
                    }

                    if (isTriggerTemplate) initializeTriggerPopup();

                    if (Object.keys($popupData.analytics?.[welcomeSelector] || {}).length !== 0) {
                        analytics.set($popupData.analytics[welcomeSelector]);
                        if (!isWelcomeTemplate) {
                            sendAnalyticsPerDay(ANALYTICS.POPUP_SHOWED);
                        }
                    }

                    if (isWelcomeTemplate) {
                        dyDataStore.set($popupData.value[welcomeSelector]);

                        if (!$dyDataStore.side.sms && Boolean(getCookie(COOKIE_VALUE.SUBSCRIBER))) {
                            return;
                        }

                        expSetRightSide();

                        const welcomeDataTimer = $popupData.value?.[welcomeSelector]?.timer;

                        if (welcomeDataTimer > 0) {
                            clearTimeout(timer);
                            timer = setTimeout(() => {
                                modal.initialize(Boolean($dyDataStore.isOpen));
                                openExitPopup();
                            }, welcomeDataTimer);
                        } else {
                            modal.initialize(Boolean($dyDataStore.isOpen));
                            openExitPopup();
                        }
                    } else {
                        openExitPopup();
                    }
                },
            );
        } catch (error) {
            logWithSentry(error as Error);
        }
    });

    setContext<ContextType>(key, {
        onEmailSubscribed,
        onEmailRejected,
    });

    $: isReminder = Boolean($dyDataStore.reminder?.[$modal.state]?.title);
</script>

<style src="./style.css">
</style>

<svelte:window on:popupopen={handlePopupOpen} on:mouseout={handleExitPopup} />

<div>
    {#if $modal.isInitialized && $modal.isVisible}
        {#if $modal.view === VIEW.MAIN}
            <BodyDesktop {instance} {instanceDY} {extraProps} />
        {/if}

        {#if isReminder && $modal.view === VIEW.REMINDER}
            <MiniReminder />
        {/if}
    {/if}
</div>
